import { mdiChevronDown, mdiChevronUp } from '@mdi/js';
import { Icon } from '@mdi/react';
import * as React from 'react';
import { useLocation } from 'react-router-dom';
import { AppSidebarCollapse } from '../app-sidebar.styles';
import * as S from './sidebar-nav.styles';

export interface ISidebarNavGroupProps {
    /** The sidebar is collapsed to its icon rail. */
    rail: boolean;
    /** Section heading. Without one the group is a plain run of items (the top of the nav). */
    label?: string;
    /** The heading opens and closes the group (File Cabinet). */
    collapsible?: boolean;
    /** Initial state of a collapsible group. */
    defaultOpen?: boolean;
    /** One of the group's routes is current — a collapsible group opens itself to show it. */
    activeWithin?: boolean;
    children?: React.ReactNode;
}

/**
 * A section of an app's sidebar. In the rail the heading becomes a rule and every item shows as an
 * icon, open or not — a closed group would otherwise leave its pages unreachable from the rail.
 */
export const SidebarNavGroup: React.FunctionComponent<ISidebarNavGroupProps> = props => {
    const { rail, label, collapsible, defaultOpen = false, activeWithin = false, children } = props;
    // `null` until the user clicks: until then the route decides, after that the click does.
    const [clicked, setClicked] = React.useState<boolean | null>(null);
    const open = clicked ?? (activeWithin || defaultOpen);

    // Entering the group, leaving it, or moving between its pages hands the decision back to the
    // route — so the page you are on is never left hidden in a group you closed earlier (closed on
    // Mail, then Fax picked from the rail). Navigating elsewhere keeps a group you opened by hand.
    const { pathname } = useLocation();
    const activePath = activeWithin ? pathname : null;
    React.useEffect(() => setClicked(null), [activePath]);

    const renderHeading = (): React.ReactNode => {
        if (!label) {
            return null;
        }
        if (rail) {
            return <S.NavGroupDivider />;
        }
        if (!collapsible) {
            return <S.NavGroupLabel>{label}</S.NavGroupLabel>;
        }
        return (
            <S.NavGroupToggle variant="unstyled" aria-expanded={open} onClick={(): void => setClicked(!open)}>
                <span>{label}</span>
                <Icon path={open ? mdiChevronUp : mdiChevronDown} size="18px" />
            </S.NavGroupToggle>
        );
    };

    const items = <S.NavItems>{children}</S.NavItems>;

    return (
        <S.NavGroup role={label ? 'group' : undefined} aria-label={label}>
            {renderHeading()}
            {collapsible && !rail ? (
                <AppSidebarCollapse in={open} timeout="auto" unmountOnExit={false}>
                    {items}
                </AppSidebarCollapse>
            ) : (
                items
            )}
        </S.NavGroup>
    );
};
